import { createContext, useContext, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import ForcePasswordChange from "@/components/ForcePasswordChange";

const AuthContext = createContext(null);
const INACTIVITY_MS = 30 * 60 * 1000; // automatisch uitloggen na 30 min inactiviteit
const TOKEN_KEY = "clazing_token";
const USER_KEY = "clazing_user";
const BULKY_CACHE_KEYS = ["clazing_productie_app_v1", "clazing_appdata_cache"];

const clearBulkyCaches = () => {
  BULKY_CACHE_KEYS.forEach((key) => localStorage.removeItem(key));
};

export const getAuthToken = () => (
  localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY)
);

export const saveAuthToken = (token) => {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    sessionStorage.removeItem(TOKEN_KEY);
    return;
  } catch (error) {
    clearBulkyCaches();
    try {
      localStorage.setItem(TOKEN_KEY, token);
      sessionStorage.removeItem(TOKEN_KEY);
      return;
    } catch (retryError) {
      sessionStorage.setItem(TOKEN_KEY, token);
    }
  }
};

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking
  const [ready, setReady] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    // Alleen oude lokale voorraadkopieën verdwijnen hier. Cloudgegevens, accounts en de
    // inlogsessie blijven intact; dit voorkomt dat een oude cache ooit de token blokkeert.
    clearBulkyCaches();
  }, []);

  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      setUser(false);
      setReady(true);
      return;
    }
    const cached = (() => { try { return JSON.parse(localStorage.getItem("clazing_user") || "null"); } catch { return null; } })();
    api
      .get("/auth/me")
      .then((res) => { setUser(res.data); try { localStorage.setItem("clazing_user", JSON.stringify(res.data)); } catch (e) { /* quota */ } })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          localStorage.removeItem(TOKEN_KEY);
          sessionStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
          setUser(false);
        } else if (cached && typeof cached === "object") {
          setUser(cached); // offline / netwerkfout: sessie behouden op basis van gecachte gebruiker
        } else {
          setUser(false);
        }
      })
      .finally(() => setReady(true));
  }, []);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    saveAuthToken(res.data.token);
    try { localStorage.setItem("clazing_user", JSON.stringify(res.data.user)); } catch (e) { /* quota */ }
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = (reason) => {
    localStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(false);
    if (reason === "inactief") toast.info("Je bent automatisch uitgelogd na 30 minuten inactiviteit. Log opnieuw in.");
  };

  const markPasswordChanged = () => setUser((u) => (u && typeof u === "object" ? { ...u, mustChangePassword: false } : u));

  // Automatisch uitloggen na 30 minuten zonder activiteit (muis, toetsen, scroll, tap).
  // Uitzondering: de kiosk-rol "urenregistratie" (kantine-tablet) blijft altijd ingelogd
  // zodat medewerkers makkelijk kunnen in-/uitklokken.
  useEffect(() => {
    if (!user || typeof user !== "object") return;
    if (user.role === "urenregistratie") return;
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];
    const reset = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => logout("inactief"), INACTIVITY_MS);
    };
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      events.forEach((e) => window.removeEventListener(e, reset));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, ready, login, logout }}>
      {children}
      {user && typeof user === "object" && user.mustChangePassword && (
        <ForcePasswordChange onDone={markPasswordChanged} onLogout={() => logout()} />
      )}
    </AuthContext.Provider>
  );
}
